/**
 * #604 — ¿la unidad de este ítem la dijo un humano, o la puso el modelo?
 *
 * ===========================================================================
 * LANE PARITY — este archivo tiene un GEMELO BYTE-EQUIVALENTE en
 * `mcp-server/src/utils/unitProvenance.js`.
 * ===========================================================================
 * No se importan entre sí a propósito: `src/**` es el bundle del browser y
 * `mcp-server/**` es un paquete Node aparte (misma razón que `auditRef.js`,
 * `materialSplit.js`, `rawTextFidelity.js`). El test de paridad
 * `mcp-server/tests/tools/unitProvenanceLaneParity.test.js` corre AMBAS copias
 * contra la misma tabla de casos. Si tocás una, tocá la otra.
 *
 * ---------------------------------------------------------------------------
 * LO MEDIDO (#604, 6 de 6 corridas contra el agente vivo)
 * ---------------------------------------------------------------------------
 * El técnico dictó, textual:
 *
 *     "Son 6 codos de 45 de una pulgada, 20 metros de cable THHN #12 y
 *      2 conectores de cable armado de 3/4."
 *
 * Unidad dicha: SOLO para el cable ("metros"). Lo que quedó guardado:
 *
 *     6 codos ............... unit="unidad"    ← nadie la dijo
 *     20 metros de cable .... unit="metros"    ← sí la dijo
 *     2 conectores .......... unit="unidad"    ← nadie la dijo
 *
 * Y `ambiguous_items_count` = 0. O sea: el contador dijo que no hubo nada
 * ambiguo, porque hasta ahora sólo sabía detectar la AUSENCIA de unidad. Un
 * modelo que rellena el campo pasa por al lado del contador sin tocarlo.
 *
 * ---------------------------------------------------------------------------
 * POR QUÉ ESTO NO SE ARREGLA CON UNA MEJOR DESCRIPCIÓN EN EL inputSchema
 * ---------------------------------------------------------------------------
 * Ya está escrita, y es explícita: *"REQUIRED, y tiene que venir de un humano —
 * NO hay default y no hay guess seguro. (...) Si no podés preguntar, OMITÍ este
 * campo. Omitir es CORRECTO; adivinar no."* Con eso puesto, el agente PREGUNTÓ
 * la unidad en el turno 1 y, al no recibirla, la rellenó igual. Y el guard es
 * NO DETERMINISTA: mismo brief y mismo modelo, tres minutos aparte, una corrida
 * inventa `unit:"rollo"` y la otra se niega y pregunta. Una regla que se cumple
 * a veces no es una regla.
 *
 * Regla del proyecto: **si es server-knowable, lo pone (o lo verifica) el
 * server** (wiki/pages/no-es-el-modelo-somos-nosotros.md — nueve casos).
 *
 * ---------------------------------------------------------------------------
 * QUÉ PUEDE PROBAR ESTE MÓDULO, Y QUÉ NO
 * ---------------------------------------------------------------------------
 * Compara DOS STRINGS QUE EL SERVER YA TIENE: la unidad que vino en el ítem y
 * el dictado original (`material_requests.source_input_text`). Es el mismo
 * mecanismo server-knowable de #369 para `raw_text`, aplicado al otro campo.
 * No le pregunta nada al modelo y no depende de ningún auto-reporte.
 *
 * Los límites, de frente:
 *
 *  - **No prueba que la palabra fuera usada COMO unidad.** Si el líder dijo
 *    "codos de una pulgada" y el modelo guarda `unit:"pulgada"`, esto lo da por
 *    dictado. El server puede probar que la palabra se dijo, no qué modificaba.
 *  - **Las abreviaturas dan falso positivo de invención**: "mts" contra un
 *    dictado que dice "metros" se marca inventada y el agente termina
 *    preguntando. Es el lado correcto para equivocarse — el issue lo dice
 *    explícito: preguntar es mejor que adivinar.
 *  - **Sin `source_input_text` no hay contra qué comparar** (`unverifiable`).
 *    Eso NO es un aprobado tácito: es que el server no puede afirmar nada. Y el
 *    modelo también controla ese campo, así que tampoco se puede tratar como
 *    prueba en contra (mismo recorte que documenta rawTextFidelity.js).
 */

/**
 * Normaliza para comparar: minúsculas, sin acentos, sin puntuación, espacios
 * colapsados. Idéntico criterio al de `rawTextFidelity.normalizeForComparison`
 * — se repite acá para que este módulo no dependa de otro (el gemelo web no
 * tiene ese archivo al lado).
 */
export const normalizeUnitText = (text) => {
  if (text == null) return '';
  return String(text)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Plural castellano, versión mínima y deliberadamente conservadora: "metros" →
 * "metro", "unidades" → "unidad", "galones" → "galon". No intenta ser una
 * lematización — sólo hace que "20 metros" en el dictado valide un `unit`
 * guardado como "metro" (y al revés), que es la variación que la medición
 * mostró de verdad ("metros" / "metro" en la misma tabla del issue).
 */
export const singularizeToken = (token) => {
  if (token.length > 3 && token.endsWith('es')) return token.slice(0, -2);
  if (token.length > 2 && token.endsWith('s')) return token.slice(0, -1);
  return token;
};

const tokenSet = (text) => {
  const out = new Set();
  for (const t of normalizeUnitText(text).split(' ')) {
    if (!t) continue;
    out.add(t);
    out.add(singularizeToken(t));
  }
  return out;
};

/** Procedencia posible de la unidad de un ítem. */
export const UNIT_SOURCE = {
  /** Nadie mandó unidad. Ausencia — es lo que #356 ya detectaba. */
  ABSENT: 'absent',
  /** La palabra aparece en el dictado original: un humano la dijo. */
  DICTATED: 'dictated',
  /** Hay dictado original y la unidad NO aparece ahí: la puso el modelo. */
  INVENTED: 'invented',
  /** No se guardó el dictado original: no hay contra qué comparar. */
  UNVERIFIABLE: 'unverifiable',
};

/**
 * @param {unknown} suppliedUnit       item.unit tal como vino en la llamada
 * @param {unknown} sourceInputText    material_requests.source_input_text
 * @returns {string} uno de UNIT_SOURCE
 */
export const classifyUnitProvenance = (suppliedUnit, sourceInputText) => {
  const unit = typeof suppliedUnit === 'string' ? suppliedUnit.trim() : '';
  if (unit === '') return UNIT_SOURCE.ABSENT;

  const src = typeof sourceInputText === 'string' ? sourceInputText.trim() : '';
  if (src === '') return UNIT_SOURCE.UNVERIFIABLE;

  const unitTokens = normalizeUnitText(unit).split(' ').filter(Boolean);
  if (unitTokens.length === 0) return UNIT_SOURCE.ABSENT;

  const said = tokenSet(src);
  // Una unidad de varias palabras ("metro lineal") sólo cuenta como dicha si
  // TODAS sus palabras se dijeron. Bastar con una sola dejaría pasar
  // "metro cúbico" porque el líder dijo "metro".
  const allSaid = unitTokens.every((t) => said.has(t) || said.has(singularizeToken(t)));
  return allSaid ? UNIT_SOURCE.DICTATED : UNIT_SOURCE.INVENTED;
};

/**
 * El sello que deja el server cuando ÉL puso la unidad desde el catálogo. Vive
 * en el namespace reservado de tags de máquina (#368): el llamador no lo puede
 * forjar, así que un ítem que lo trae es prueba de que lo escribió el servidor.
 */
export const unitFromCatalogNote = (catalogUnit) =>
  `[UNIDAD-CATALOGO: la unidad "${catalogUnit}" NO la dijo el líder — la puso el servidor desde el catálogo de inventario (#604)]`;

/**
 * El sello para la unidad que el modelo inventó y que el catálogo no puede
 * resolver. Se reusa el vocabulario de #356 a propósito: para el comprador el
 * caso es el mismo — nadie dijo en qué unidad va esto.
 */
export const INVENTED_UNIT_NOTE =
  '[AMBIGUO: la unidad no aparece en el dictado del líder — la puso el asistente, confirmar antes de comprar (#604)]';

const unitProvenance = {
  UNIT_SOURCE,
  normalizeUnitText,
  singularizeToken,
  classifyUnitProvenance,
  unitFromCatalogNote,
  INVENTED_UNIT_NOTE,
};

export default unitProvenance;
